/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { pretty } from "@vlinder-web/utils-react";
import { MASQ_REPORT } from "../graphql/getMasqReport.query";
import { GET_REPORT_STATUS } from "../graphql/getReportStatus.query";

const is = require("is_js");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const REQUIRED_CSV_FIELDS = [
  "CostumeID",
  "MasqID",
  "Email",
  "FirstName",
  "LastName",
  "TotalAmount",
  "AmountPaid",
  "PaidTTD",
  "PaidUSD",
  "RefundedTTD",
  "RefundedUSD",
  "PaymentStatus",
  "PaymentDate",
  "Section",
  "SectionOptType",
];

export interface IMasqReportMethods extends IPluginMethodMap {
  getMasqReport(args: any): Promise<any>;
}

export class MasqReportService implements IAgentPlugin {
  readonly methods: IMasqReportMethods;
  private pollingTime = 10000;

  constructor() {
    this.methods = {
      getMasqReport: this.getMasqReport.bind(this),
    };
  }

  async getMasqReport(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    let filters: any = {};
    if (args?.filters && typeof args.filters === "object") {
      filters = { ...args.filters };
    }

    const pageCriteria = args?.pageFilters || args?.criteria;
    if (pageCriteria && typeof pageCriteria === "object") {
      if (pageCriteria.presentation && !filters.presentation) {
        filters.presentation = {
          eq: pageCriteria.presentation,
        };
      }
      if (pageCriteria.section && !filters.section) {
        filters.section = {
          eq: pageCriteria.section,
        };
      }
      if (pageCriteria.paymentStats && !filters.paymentStats) {
        filters.paymentStats = {
          eq: pageCriteria.paymentStats,
        };
      }
    }

    let fields: string[] = [...REQUIRED_CSV_FIELDS];
    let costumeAttributeFilter: string[] = [];

    try {
      if (client) {
        if (args?.presentation) {
          filters = {
            ...filters,
            presentation: {
              eq: args.presentation,
            },
          };

          this.pollingTime = 10000;
        }

        if (args?.section) {
          filters = {
            ...filters,
            section: {
              eq: args?.section,
            },
          };

          this.pollingTime = 5000;
        }

        if (args?.sectionOptType) {
          filters = {
            ...filters,
            items___costumeType: {
              eq: args?.sectionOptType,
            },
          };

          this.pollingTime = 5000;
        }

        if (Array.isArray(args?.fields) && args.fields.length > 0) {
          fields = Array.from(new Set([...REQUIRED_CSV_FIELDS, ...args.fields]));
          this.pollingTime = 5000;
        }

        if (
          Array.isArray(args?.costumeAttributeFilter) &&
          args.costumeAttributeFilter.length > 0
        ) {
          costumeAttributeFilter = args.costumeAttributeFilter;
          this.pollingTime = 5000;
        }

        const costumeDataRes = await client.query({
          query: MASQ_REPORT,
          variables: {
            filters: filters,
            fields: fields,
            costumeAttributeFilter: costumeAttributeFilter,
          },
          fetchPolicy: "network-only",
        });

        const resultData = costumeDataRes?.data?.getDetailedCostumeData;

        if (resultData?.csvUrl && is.existy(resultData?.csvUrl)) {
          return resultData;
        }

        if (resultData?.jobId && is.existy(resultData?.jobId)) {
          return this.checkStatus(resultData.jobId);
        }

        throw new Error("Some unexpected error occurred");
      }
    } catch (err: any) {
      console.error("getMasqReport error ", err);

      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private async checkStatus(jobId: string, retries = 100) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      const reportRes = await client.query({
        query: GET_REPORT_STATUS,
        variables: {
          jobId: jobId,
        },
        fetchPolicy: "network-only",
      });

      const reportStatus = reportRes?.data?.getReportStatus;

      if (reportStatus?.csvUrl && is.existy(reportStatus?.csvUrl)) {
        return reportStatus;
      }

      if (reportStatus?.message === "success") {
        return reportStatus;
      } else if (
        reportStatus?.message === "pending" ||
        reportStatus?.message === "PaymentNotComplete" ||
        reportStatus?.message === "InProgress" ||
        reportStatus?.message === "processing" ||
        (!reportStatus?.csvUrl &&
          reportStatus?.message !== "failed" &&
          reportStatus?.message !== "error")
      ) {
        if (retries > 0) {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              this.checkStatus(jobId, retries - 1)
                .then(resolve)
                .catch(reject);
            }, this.pollingTime || 10000);
          });
        } else {
          throw new Error("Exceeded retry limit for checking status");
        }
      } else {
        throw new Error(
          reportStatus?.message || "Some unexpected error occurred"
        );
      }
    } catch (err: any) {
      console.error("checkStatus error ", err);

      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
